import { DataTableBlock, type ListPageColumn } from '@/components/templates/list-page'
import { Button } from '@/components/ui/button'
import { displayDateTime } from '@/lib/dates'
import { EmailLogDetailSheet } from './EmailLogDetailSheet'
import { useEmailSendPages } from './EmailLogPagination'
import { emailSendColumns, type EmailSend } from './EmailLogTable'
import { useEmailLogSelection } from './useEmailLogSelection'

/** Every email sent to an organisation and to the people with a live Affiliation at it. */
export function OrganizationEmailHistory({ organizationId }: { organizationId: number }) {
  const selection = useEmailLogSelection(`organization:${organizationId}`)
  const { query, pagination } = useEmailSendPages(
    `/api/v1/email_sends?organization_id=${organizationId}`,
  )

  return (
    <section className="space-y-3">
      <div>
        <h3 className="text-body font-semibold text-foreground">Email history</h3>
        <p className="text-body-sm text-muted-foreground">
          Emails sent to this organisation and to its people.
        </p>
      </div>
      <div className="overflow-hidden rounded-md border border-border bg-card">
        <DataTableBlock
          table={{
            rows: query.data?.email_sends ?? [],
            columns: organizationEmailColumns(selection.open),
            getRowId: (send) => send.id,
            onRowOpen: (send) => selection.open(send.id),
            getRowLabel: (send) => send.subject ?? send.kind_label,
            state: query.isLoading ? 'loading' : query.isError ? 'error' : 'ready',
            loadingTitle: 'Loading email history',
            errorTitle: 'Email history unavailable',
            emptyTitle: 'No emails recorded',
            footer: pagination,
          }}
        />
      </div>
      <EmailLogDetailSheet id={selection.id} scope={selection.scope} onClose={selection.close} />
    </section>
  )
}

function organizationEmailColumns(open: (id: number) => void): ListPageColumn<EmailSend>[] {
  return [
    {
      id: 'sent-at',
      header: 'Sent',
      className: 'whitespace-nowrap',
      cell: (send) => displayDateTime(send.sent_at),
    },
    {
      id: 'recipient',
      header: 'Recipient',
      cell: (send) => (
        <div className="flex flex-col">
          <span className="font-medium">
            {send.recipient_display_name ?? send.recipient_email ?? 'No address'}
          </span>
          {send.recipient_display_name && send.recipient_email && (
            <span className="text-caption text-muted-foreground">{send.recipient_email}</span>
          )}
        </div>
      ),
    },
    {
      id: 'subject',
      header: 'Subject',
      cell: (send) => (
        <Button
          type="button"
          variant="link"
          size="inline"
          className="whitespace-normal text-left"
          onClick={() => open(send.id)}
        >
          {send.subject ?? send.kind_label}
        </Button>
      ),
    },
    emailSendColumns(false).find((column) => column.id === 'status')!,
  ]
}
